import { CirclePlus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { AvatarSprite } from '@/app/shell/avatar-sprite'
import { UserAvatar } from '@/graphql/schema'
import { cn } from '@/shared/lib/utils'
import { Button } from '@/shared/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu'

const MAX_VISIBLE_AVATARS = 5

export type WorkspaceSwitcherPresenceUser = {
  avatar: UserAvatar | null
  displayName: string
  id: string
  isMuted?: boolean
  status: 'online' | 'away'
}

export type WorkspaceSwitcherWorkspace = {
  id: string
  isReadOnly?: boolean | null
  name: string
  organizationId?: string | null
  organizationName?: string | null
  onlineUsers: Array<WorkspaceSwitcherPresenceUser>
  workspaceRole?: string | null
}

export type WorkspacePresenceWorkspaceSwitcherProps = {
  currentWorkspaceId: string
  onAddWorkspace: () => void
  onWorkspaceChange: (workspaceId: string) => void
  workspaces: Array<WorkspaceSwitcherWorkspace>
}

function getCurrentWorkspace({
  currentWorkspaceId,
  workspaces,
}: {
  currentWorkspaceId: string
  workspaces: Array<WorkspaceSwitcherWorkspace>
}) {
  if (workspaces.length === 0) {
    return null
  }

  return (
    workspaces.find((workspace) => workspace.id === currentWorkspaceId) ??
    workspaces[0]
  )
}

function WorkspaceAvatarStack({
  className,
  users,
}: {
  className?: string
  users: Array<WorkspaceSwitcherPresenceUser>
}) {
  const { t } = useTranslation('workspacePresence')
  const visibleUsers = users.slice(0, MAX_VISIBLE_AVATARS)
  const overflowCount = users.length - visibleUsers.length

  if (users.length === 0) {
    return null
  }

  return (
    <span
      aria-label={t('usersOnline', { count: users.length })}
      className={cn('inline-flex items-center pl-2', className)}
    >
      {visibleUsers.map((user) => (
        <span
          key={user.id}
          aria-label={t(`user${user.status === 'away' ? 'Away' : 'Online'}`, {
            name: user.displayName,
          })}
          role="img"
          title={t(`user${user.status === 'away' ? 'Away' : 'Online'}`, {
            name: user.displayName,
          })}
          className={cn(
            'relative -ml-2 inline-flex size-7 items-center justify-center rounded-full border-2 border-background bg-muted shadow-sm first:ml-0',
            user.status === 'away' && 'opacity-65',
            user.isMuted && 'grayscale',
          )}
        >
          <AvatarSprite
            avatar={user.avatar ?? UserAvatar.WomanChestnutGreen}
            size={24}
            className="size-6 rounded-full"
          />
          <span
            aria-hidden="true"
            className={cn(
              'absolute right-0 bottom-0 size-2 rounded-full border border-background',
              user.status === 'online' ? 'bg-emerald-500' : 'bg-amber-500',
            )}
          />
        </span>
      ))}
      {overflowCount > 0 ? (
        <span
          aria-label={t('moreUsersOnline', { count: overflowCount })}
          className="-ml-2 inline-flex size-7 items-center justify-center rounded-full border-2 border-background bg-muted text-[11px] font-medium text-muted-foreground shadow-sm"
          title={t('moreUsersOnline', { count: overflowCount })}
        >
          +{overflowCount}
        </span>
      ) : null}
    </span>
  )
}

function WorkspaceOption({
  workspace,
}: {
  workspace: WorkspaceSwitcherWorkspace
}) {
  const { t } = useTranslation('workspacePresence')

  return (
    <span className="flex min-w-0 flex-1 items-center justify-between gap-3">
      <span className="min-w-0">
        <span className="block truncate font-medium">{workspace.name}</span>
        <span className="block text-xs text-muted-foreground">
          {t('onlineCount', { count: workspace.onlineUsers.length })}
          {workspace.workspaceRole
            ? ` · ${t('roleAccess', {
                role: t(
                  `generalSettings:employeeManagement.roles.${workspace.workspaceRole.toLowerCase()}`,
                ),
              })}`
            : ''}
        </span>
      </span>
      {workspace.isReadOnly ? (
        <span className="shrink-0 rounded border px-1.5 py-0.5 text-[10px] text-muted-foreground">
          {t('readOnly')}
        </span>
      ) : null}
      <WorkspaceAvatarStack
        className="shrink-0"
        users={workspace.onlineUsers}
      />
    </span>
  )
}

function groupWorkspaces(
  workspaces: Array<WorkspaceSwitcherWorkspace>,
  fallbackLabel: string,
) {
  const groups = new Map<
    string,
    { id: string; label: string; workspaces: Array<WorkspaceSwitcherWorkspace> }
  >()

  for (const workspace of workspaces) {
    const id = workspace.organizationId ?? workspace.organizationName ?? 'other'
    const group = groups.get(id) ?? {
      id,
      label: workspace.organizationName ?? fallbackLabel,
      workspaces: [],
    }
    group.workspaces.push(workspace)
    groups.set(id, group)
  }

  return [...groups.values()]
}

export function WorkspacePresenceWorkspaceSwitcher({
  currentWorkspaceId,
  onAddWorkspace,
  onWorkspaceChange,
  workspaces,
}: WorkspacePresenceWorkspaceSwitcherProps) {
  const { t } = useTranslation('workspacePresence')
  const currentWorkspace = getCurrentWorkspace({
    currentWorkspaceId,
    workspaces,
  })
  const workspaceGroups = groupWorkspaces(workspaces, t('otherOrganization'))

  if (!currentWorkspace) {
    return null
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="h-auto min-w-48 justify-between px-3 py-2 shadow-none"
          aria-label={t('chooseWorkspace')}
        >
          <span className="flex min-w-0 flex-col items-start justify-center gap-1.5">
            <span className="flex max-w-44 items-center gap-1.5 text-lg font-medium leading-none">
              <span className="truncate">{currentWorkspace.name}</span>
              {currentWorkspace.isReadOnly ? (
                <span className="rounded border px-1 text-[9px] text-muted-foreground">
                  {t('readOnly')}
                </span>
              ) : null}
            </span>
            <WorkspaceAvatarStack users={currentWorkspace.onlineUsers} />
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-80">
        <DropdownMenuLabel className="text-xs text-muted-foreground">
          {t('chooseWorkspace')}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuRadioGroup
          value={currentWorkspace.id}
          onValueChange={onWorkspaceChange}
        >
          {workspaceGroups.flatMap((group) => [
            <DropdownMenuLabel
              key={`${group.id}-label`}
              className="pt-2 text-[11px] font-semibold text-muted-foreground"
            >
              {group.label}
            </DropdownMenuLabel>,
            ...group.workspaces.map((workspace) => (
              <DropdownMenuRadioItem
                key={workspace.id}
                value={workspace.id}
                className="items-center py-2"
              >
                <WorkspaceOption workspace={workspace} />
              </DropdownMenuRadioItem>
            )),
          ])}
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={onAddWorkspace}>
          <CirclePlus className="size-4" />
          {t('addWorkspace')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
